import { DestroyRef, inject, Injectable, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { LanguageIso } from '@mkp/shared/models';
import { BehaviorSubject, combineLatest, from, Observable, of } from 'rxjs';
import { catchError, filter, switchMap, take, tap } from 'rxjs/operators';
import { ZENDESK_HOST } from './zendesk-host';
import { WindowWithZendesk, ZenDeskActions, ZenDeskCommands } from './zendesk.model';

// new classes can be added to move the launcher widget around when needed
type ZendeskLauncherClassName = 'internal-ats';
const ZENDESK_LAUNCHER_IFRAME_CSS_SELECTOR = 'iframe#launcher';

/**
 * Service to interact with Zendesk widget.
 * @desc https://jobcloud.atlassian.net/wiki/spaces/CORE/pages/84849623279/Zendesk+Implementation
 */
@Injectable({
  providedIn: 'root',
})
export class ZendeskService {
  private readonly window = inject(Window) as WindowWithZendesk;
  private readonly host = inject(ZENDESK_HOST);
  private readonly destroy = inject(DestroyRef);

  private readonly isInitialized$ = new BehaviorSubject<boolean>(false);
  private isInitializing = false;
  readonly launcherClassNames = signal<ZendeskLauncherClassName[]>([]);

  constructor() {
    this.listenToConsentChange();
  }

  private listenToConsentChange() {
    this.host.consentLoaded$
      .pipe(
        filter(Boolean),
        switchMap(() =>
          combineLatest([this.host.functionalConsent$, this.host.isBoUser$]).pipe(
            takeUntilDestroyed(this.destroy),
            tap(([functional, isBoUser]) => {
              if (!isBoUser && functional) {
                this.initializeZendeskIfNeeded$()
                  .pipe(tap(() => this.enableCookies()))
                  .subscribe();
              } else {
                this.disableCookies();
              }
            })
          )
        )
      )
      .subscribe();
  }

  private initializeZendeskIfNeeded$(): Observable<boolean> {
    return this.isInitialized$.pipe(
      take(1),
      switchMap((isInitialized) => {
        if (isInitialized) return of(true);
        if (this.isInitializing) return this.isInitialized$.pipe(filter(Boolean), take(1));

        this.isInitializing = true;
        return this.initializeZenDesk();
      })
    );
  }

  private initializeZenDesk() {
    return from(
      new Promise<boolean>((resolve) => {
        if (this.window.zE) {
          this.setInitialized(resolve, true);
        } else if (typeof this.window.initZendesk === 'function') {
          this.window.initZendesk();
          const checkZendesk = setInterval(() => {
            if (this.window.zE) {
              clearInterval(checkZendesk);
              this.setInitialized(resolve, true);
            }
          }, 100);
        } else {
          console.error('Zendesk initialization function not found');
          this.setInitialized(resolve, false);
        }
      })
    ).pipe(
      catchError((error) => {
        console.error('Failed to initialize Zendesk:', error);
        this.isInitializing = false;
        return of(false);
      })
    );
  }

  private setInitialized(resolve: (value: boolean) => void, value: boolean) {
    this.isInitialized$.next(value);
    this.isInitializing = false;
    resolve(value);
  }

  openWidget(): void {
    const openWidget = () => this.getZendesk()(ZenDeskActions.Messenger, ZenDeskCommands.Open);

    this.host.functionalConsent$
      .pipe(
        take(1),
        switchMap((functional) =>
          functional
            ? this.initializeZendeskIfNeeded$().pipe(tap(openWidget))
            : this.host.requestFunctionalConsent().pipe(
                switchMap((granted) =>
                  granted
                    ? this.host.functionalConsent$.pipe(
                        filter(Boolean),
                        take(1),
                        switchMap(() => this.initializeZendeskIfNeeded$()),
                        tap(openWidget)
                      )
                    : of(false)
                )
              )
        )
      )
      .subscribe();
  }

  private getZendesk() {
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    return this.window.zE || (() => {});
  }

  private enableCookies() {
    this.getZendesk()(ZenDeskActions.Set, ZenDeskCommands.Cookies, true);
    // if the launcher is added to the DOM after enabling cookies: add the launcher-classes to it
    this.setClassNamesToLauncherIframe(this.launcherClassNames());
  }

  private disableCookies() {
    this.getZendesk()(ZenDeskActions.Set, ZenDeskCommands.Cookies, false);
  }

  closeWidget() {
    this.getZendesk()(ZenDeskActions.Messenger, ZenDeskCommands.Close);
  }

  // see locales https://support.zendesk.com/api/v2/locales/public.json
  setLocale(locale: LanguageIso) {
    this.getZendesk()(ZenDeskActions.Set, ZenDeskCommands.Locale, locale.toLowerCase());
  }

  login(zendeskJWT: string) {
    this.initializeZendeskIfNeeded$()
      .pipe(
        filter(Boolean),
        take(1),
        tap(() => {
          this.getZendesk()(
            ZenDeskActions.Messenger,
            ZenDeskCommands.LoginUser,
            function (callback: CallableFunction) {
              callback(zendeskJWT);
            }
          );
        })
      )
      .subscribe();
  }

  toggleLauncherClass(addOrRemove: 'add' | 'remove', className: ZendeskLauncherClassName): void {
    const classNames = getNewClassNames(this.launcherClassNames(), addOrRemove, className);
    this.launcherClassNames.set(classNames);

    this.setClassNamesToLauncherIframe(this.launcherClassNames());
  }

  private setClassNamesToLauncherIframe(classNames: ZendeskLauncherClassName[]) {
    const launcherIframe = getLauncherIframe(this.window.document);

    if (launcherIframe) {
      launcherIframe.classList.value = classNames.join(' ');
    }
  }
}

const getLauncherIframe = (document: Document) =>
  document.querySelector(ZENDESK_LAUNCHER_IFRAME_CSS_SELECTOR);

const getNewClassNames = (
  classNames: ZendeskLauncherClassName[],
  addOrRemove: 'add' | 'remove',
  newClassName: ZendeskLauncherClassName
): ZendeskLauncherClassName[] =>
  addOrRemove === 'add'
    ? Array.from(new Set(classNames).add(newClassName))
    : classNames.filter((className) => className !== newClassName);
